/* eslint-disable no-nested-ternary */
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { useSnackbar } from 'src/components/snackbar';
import { useBoolean } from 'src/hooks/use-boolean';
import { useAxiosClientContext } from 'src/http/context/axios-client-context';
import { BenefitsRepository } from 'src/repositories/benefits/benefits.repository';

export type DiscountType = 'percentage' | 'value' | 'courtesy' | 'buyAndGet' | 'custom';

export const HOW_TO_USE_STEP_MAX_LENGTH = 25;

export type FormValues = {
  name: string;
  category: string;
  type: 'voucher' | 'informative' | 'parking';
  levels: string[];
  stock?: number;
  description: string;
  conditions: string;
  howToUse: string[];
  storeEvent: 'store' | 'restaurant' | 'event';
  store?: string | null;
  restaurant?: string | null;
  event?: string | null;
  discount?: number;
  discountType?: DiscountType;
  discountTitle?: string;
  discountSubtitle?: string;
  voucherType?: 'generate' | 'import' | 'uniqueCode';
  voucherCode?: string;
  banner: {
    url: string;
    type?: string;
    name?: string;
  };
  thumb: {
    url?: string;
    type?: string;
    name?: string;
  };
  startDate?: string;
  endDate?: string;
  status?: 'active' | 'inactive';
  vouchersQuantity?: number;
  parkingPromoInfo?: { value: string; label: string } | null;
  redemptionLimitPerUser?: number;
};

type ImportFeedbackSummary = {
  insertedCount: number;
  duplicateCodes: string[];
  successCount: number;
  duplicateCount: number;
  errorCount: number;
};

type VoucherStats = {
  total: number;
  redeemed: number;
  used: number;
};

export const useTableCollapse = (row: any, getData: () => void, onClose?: () => void) => {
  const api = useAxiosClientContext();
  const { toastWarning, toastSuccess, toastInfo, toastError } = useSnackbar();
  const useInvalidFile = useBoolean(false);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);

  const [hasSuccess, setHasSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  const [howToUseSteps, setHowToUseSteps] = useState<string[]>(['']);
  const [selectedStoreOption, setSelectedStoreOption] = useState<'select' | 'restaurant' | 'manual' | ''>('');
  const [selectedCodeVoucherOption, setSelectedCodeVoucherOption] = useState<'import' | 'generate' | 'uniqueCode' | ''>('');
  const [selectedVoucherOption, setSelectedVoucherOption] = useState<DiscountType | ''>('');
  const [selectedCategories, setSelectedCategories] = useState<{ label: string; value: string }[]>([]);
  const [categoriesOptions, setCategoriesOptions] = useState<{ label: string; value: string }[]>([]);
  const [levelsOptions, setLevelsOptions] = useState<{ label: string; value: string }[]>([]);
  const [storesOptions, setStoresOptions] = useState<{ label: string; value: string }[]>([]);
  const [restaurantsOptions, setRestaurantsOptions] = useState<{ label: string; value: string }[]>([]);
  const [eventsOptions] = useState<{ label: string; value: string }[]>([]);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvLineCount, setCsvLineCount] = useState<number>(0);
  const [hasInitializedLevels, setHasInitializedLevels] = useState(false);
  const [voucherStats, setVoucherStats] = useState<VoucherStats>({ total: 0, redeemed: 0, used: 0 });
  const [importFeedbackSummary, setImportFeedbackSummary] = useState<ImportFeedbackSummary | null>(null);
  const [promotionOptions, setPromotionOptions] = useState<any[]>([]);

  const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

  const formatDateToISO = (dateString?: string, type: 'start' | 'end' = 'start'): string | undefined => {
    if (!dateString || dateString.trim() === '') return undefined;
    try {
      if (dateString.includes('T') && (dateString.includes('Z') || dateString.includes('+'))) {
        const date = new Date(dateString);
        if (type === 'start') date.setHours(0, 0, 0, 0);
        else if (type === 'end') date.setHours(23, 59, 59, 999);
        return date.toISOString();
      }
      let date: Date;
      if (dateString.includes('/')) {
        const [day, month, year] = dateString.split('/');
        date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
      } else if (dateString.includes('-')) {
        date = new Date(dateString);
      } else {
        console.warn('Formato de data não reconhecido:', dateString);
        return undefined;
      }
      if (Number.isNaN(date.getTime())) {
        console.warn('Data inválida:', dateString);
        return undefined;
      }
      if (type === 'start') {
        date.setHours(0, 0, 0, 0);
      } else if (type === 'end') {
        date.setHours(23, 59, 59, 999);
      }
      return date.toISOString();
    } catch (error) {
      console.error('Erro ao formatar data:', error, 'Data original:', dateString);
      return undefined;
    }
  };

  const defaultValues = useMemo<FormValues>(
    () => ({
      name: row?.name || '',
      category: (() => {
        if (typeof row?.category === 'object' && row?.category?.$oid) return row.category.$oid;
        if (Array.isArray(row?.category) && row.category.length > 0) return row.category[0]?.$oid || row.category[0];
        return row?.category || '';
      })(),
      type: row?.type || 'informative',
      levels: row?.levels || [],
      vouchersQuantity: row?.id && row?.id !== '-' && row?.type !== 'parking' ? 0 : row?.vouchersQuantity || 0,
      parkingPromoInfo: row?.parkingPromoInfo?.id ? { value: row.parkingPromoInfo.id, label: row.parkingPromoInfo.title } : null,
      redemptionLimitPerUser: row?.redemptionLimitPerUser ?? 1,
      stock: row?.stock || 0,
      description: row?.description || '',
      conditions: row?.conditions || '',
      howToUse: row?.howToUse || [''],
      storeEvent: row?.storeEvent === 'select' ? 'store' : row?.storeEvent || 'store',
      store: (() => {
        if (typeof row?.store === 'object' && row?.store?.$oid) return row.store.$oid;
        if (Array.isArray(row?.store) && row.store.length > 0) return row.store[0]?.$oid || row.store[0];
        return row?.store || null;
      })(),
      restaurant: (() => {
        if (typeof row?.restaurant === 'object' && row?.restaurant?.$oid) return row.restaurant.$oid;
        if (Array.isArray(row?.restaurant) && row.restaurant.length > 0) return row.restaurant[0]?.$oid || row.restaurant[0];
        return row?.restaurant || null;
      })(),
      event: row?.event || null,
      discount: row?.discount || 0,
      discountType: row?.discountType || '',
      discountTitle: row?.discountTitle || '',
      discountSubtitle: row?.discountSubtitle || '',
      voucherType: row?.voucherType || 'generate',
      voucherCode: row?.voucherCode || '',
      banner: typeof row?.banner === 'object' && row?.banner !== null ? row.banner : { url: row?.banner ?? '', name: '', type: '' },
      thumb: typeof row?.thumb === 'object' && row?.thumb !== null ? row.thumb : { url: row?.thumb ?? '', name: '', type: '' },
      startDate: row?.startDate || '',
      endDate: row?.endDate || '',
      status: row?.status || 'active',
    }),
    [row]
  );

  const methods = useForm<FormValues>({
    resolver: yupResolver(
      Yup.object().shape({
        name: Yup.string().required('Preencha o nome do benefício'),
        category: Yup.string()
          .required('Selecione uma categoria'),
        type: Yup.string()
          .required('Selecione pelo menos uma opção')
          .oneOf(['informative', 'voucher', 'parking'], 'Selecione pelo menos uma opção'),
        levels: Yup.array().of(Yup.string()).required('Selecione pelo menos um nível').min(1, 'Selecione pelo menos um nível'),
        stock: Yup.number()
          .typeError('Informe uma quantidade válida de vouchers')
          .when(['type', 'voucherType'], {
            is: (type: string, voucherType: string) =>
              type === 'voucher' && voucherType === 'generate' && (!row?.id || row.id === '-'),
            then: (schema) => schema.required('Informe o estoque para gerar a lista de códigos'),
            otherwise: (schema) => schema.optional(),
          }),
        description: Yup.string().required('Preencha a descrição do benefício').max(55, 'Descrição deve ter no máximo 55 caracteres'),
        conditions: Yup.string()
          .required('Condições são obrigatórias')
          .min(5, 'Condições devem ter pelo menos 5 caracteres')
          .max(1000, 'Condições devem ter no máximo 1000 caracteres'),
        howToUse: Yup.array()
          .of(
            Yup.string()
              .required('Passo é obrigatório')
              .max(HOW_TO_USE_STEP_MAX_LENGTH, `Cada passo deve ter no máximo ${HOW_TO_USE_STEP_MAX_LENGTH} caracteres`)
          )
          .required('Como utilizar é obrigatório')
          .min(1, 'Adicione pelo menos um passo')
          .test('has-valid-steps', 'Pelo menos um passo deve ser preenchido', (value) => {
            if (!value || value.length === 0) return false;
            return value.some((step) => step && step.trim() !== '');
          }),

        storeEvent: Yup.string().when('type', {
          is: (type: string) => type !== 'parking',
          then: (schema) =>
            schema.required('Selecione pelo menos uma opção').oneOf(['store', 'restaurant', 'event'], 'Selecione uma opção válida'),
          otherwise: (schema) => schema.notRequired().nullable(),
        }),

        store: Yup.mixed()
          .nullable()
          .test('store-required', 'Loja é obrigatória', (value, context) => {
            const { storeEvent, type } = context.parent;
            if (type !== 'parking' && storeEvent === 'store') {
              return value !== null && value !== '';
            }
            return true;
          }),

        restaurant: Yup.mixed()
          .nullable()
          .test('restaurant-required', 'Restaurante é obrigatório', (value, context) => {
            const { storeEvent, type } = context.parent;
            if (type !== 'parking' && storeEvent === 'restaurant') {
              return value !== null && value !== '' && value !== undefined;
            }
            return true;
          }),

        event: Yup.mixed()
          .nullable()
          .test('event-required', 'Evento é obrigatório', (value, context) => {
            const { storeEvent, type } = context.parent;
            if (type === 'parking') return true;
            if (storeEvent === 'event') {
              return value !== null && value !== '';
            }
            return true;
          }),
        discount: Yup.number().optional(),
        discountType: Yup.string().when('type', {
          is: 'voucher',
          then: (schema) => schema.oneOf(['percentage', 'value', 'courtesy', 'buyAndGet', 'custom'], 'Tipo de desconto inválido'),
          otherwise: (schema) => schema.transform((v) => v || undefined).optional(),
        }),
        discountTitle: Yup.string().when(['type', 'discountType'], {
          is: (type: string, discountType: string) => type === 'voucher' && discountType === 'custom',
          then: (schema) => schema.trim().required('Preencha o título').max(15, 'Título deve ter no máximo 15 caracteres'),
          otherwise: (schema) => schema.strip(),
        }),
        discountSubtitle: Yup.string().when(['type', 'discountType'], {
          is: (type: string, discountType: string) => type === 'voucher' && discountType === 'custom',
          then: (schema) => schema.trim().required('Preencha o subtítulo').max(8, 'Subtítulo deve ter no máximo 8 caracteres'),
          otherwise: (schema) => schema.strip(),
        }),
        voucherType: Yup.string().when('type', {
          is: 'voucher',
          then: (schema) => schema.oneOf(['generate', 'import', 'uniqueCode'], 'Tipo de voucher inválido'),
          otherwise: (schema) => schema.transform((v) => v || undefined).optional(),
        }),
        voucherCode: Yup.string().optional(),
        banner: Yup.object()
          .shape({
            url: Yup.string().test('banner-url-required', 'Banner é obrigatório', (value) => !!(value && value.trim() !== '')),
            type: Yup.string(),
            name: Yup.string(),
          })
          .required('Banner é obrigatório')
          .test('banner-valid', 'Banner é obrigatório', (value) => !!(value && value.url && value.url.trim() !== '')),
        thumb: Yup.object()
          .shape({
            url: Yup.string().nullable(),
            type: Yup.string().nullable(),
            name: Yup.string().nullable(),
          })
          .when('event', {
            is: (event: any) => event && event !== '',
            then: (schema) =>
              schema
                .shape({
                  url: Yup.string().required('Thumb é obrigatória quando evento for selecionado'),
                })
                .required('Thumb é obrigatória quando evento for selecionado'),
            otherwise: (schema) => schema.notRequired(),
          }),
        startDate: Yup.string().optional(),
        endDate: Yup.string().optional(),
        status: Yup.string().oneOf(['active', 'inactive']).optional(),
        vouchersQuantity: Yup.number()
          .typeError('Insira um número válido')
          .nullable()
          .transform((value) => (Number.isNaN(value) ? undefined : value))
          .test('required-on-create', 'Quantidade de vouchers é obrigatória', (value) => {
            const isEdit = row?.id && row.id !== '-';
            if (!isEdit) {
              return value !== undefined && value !== null;
            }
            return true;
          }),
        parkingPromoInfo: Yup.mixed().when('type', {
          is: 'parking',
          then: (schema) => schema.required('Selecione um código promocional'),
          otherwise: (schema) => schema.nullable().notRequired(),
        }),
        redemptionLimitPerUser: Yup.number().when('type', {
          is: (type: string) => type === 'voucher' || type === 'parking',
          then: (schema) =>
            schema
              .typeError('Insira um número válido')
              .integer('Informe uma quantidade inteira de resgates')
              .min(1, 'O limite de resgates deve ser de pelo menos 1')
              .required('Informe o limite de resgates por usuário'),
          otherwise: (schema) => schema.optional(),
        }),
      })
    ) as any,
    defaultValues,
  });

  const {
    handleSubmit,
    setValue,
    formState: { isSubmitting, errors },
    watch,
  } = methods;

  const values = watch();

  const isEditMode = row && row.id !== '-';

  // Validações de voucher
  const validateVoucherType = (data: FormValues): string | null => {
    if (!selectedVoucherOption) {
      return 'Selecione o tipo de desconto';
    }
    if (['percentage', 'value'].includes(data.discountType || '') && (!data.discount || data.discount <= 0)) {
      return 'O valor do desconto deve ser maior que zero';
    }
    if (!selectedCodeVoucherOption) {
      return 'Selecione o tipo de código do voucher';
    }
    return null;
  };

  const validateVoucherFiles = (voucherType: string, vouchersQuantity?: number, voucherCode?: string): string | null => {
    if (selectedCodeVoucherOption === 'uniqueCode' && !voucherCode) {
      return 'Código do voucher é obrigatório para o tipo de voucher selecionado';
    }
    return null;
  };

  const validateParkingType = (data: FormValues): string | null => {
    if (!data.parkingPromoInfo || !data.parkingPromoInfo.value) {
      return 'Preencha o código promocional fornecido pela WPS';
    }
    return null;
  };

  // Transformação de dados
  const transformFormData = (data: FormValues) => ({
    ...data,
    category: (() => {
      if (Array.isArray(data.category) && data.category.length > 0) {
        return data.category[0];
      }
      if (typeof data.category === 'object' && (data.category as any)?.value) {
        return (data.category as any).value;
      }
      return data.category;
    })(),
    store: typeof data.store === 'object' && (data.store as any)?.value ? (data.store as any).value : data.store,
    restaurant: typeof data.restaurant === 'object' && (data.restaurant as any)?.value ? (data.restaurant as any).value : data.restaurant,
    event: typeof data.event === 'object' && (data.event as any)?.value ? (data.event as any).value : data.event,
  });

  const buildFormattedData = (data: FormValues, transformedData: any) => {
    const formattedData: any = {
      banner: data.banner?.url || '',
      name: data.name,
      category: transformedData.category,
      type: data.type,
      ...(data.type !== 'parking' && { storeEvent: data.storeEvent }),
      startDate: formatDateToISO(data.startDate, 'start'),
      endDate: formatDateToISO(data.endDate, 'end'),
      levels: data.levels,
      description: data.description,
      howToUse: data.howToUse.map((step) => step.slice(0, HOW_TO_USE_STEP_MAX_LENGTH)),
      conditions: data.conditions,
      status: data.status || 'active',
    };

    if (data.type === 'voucher') {
      formattedData.discountType = data.discountType;

      if (data.discountType === 'custom') {
        formattedData.discountTitle = data.discountTitle?.trim();
        formattedData.discountSubtitle = data.discountSubtitle?.trim();
      }

      if (['percentage', 'value'].includes(data.discountType || '') && data.discount !== null && data.discount !== undefined) {
        formattedData.discount = Number(data.discount);
      }

      formattedData.voucherType = data.voucherType;

      if (selectedCodeVoucherOption === 'uniqueCode' && data.voucherCode) {
        formattedData.voucherCode = data.voucherCode;
      }

      if (!isEditMode && data.stock !== null && data.stock !== undefined) {
        const stockValue = Number(data.stock);
        formattedData.stock = stockValue;
        formattedData.vouchersQuantity = stockValue;
      }

      if (!isEditMode && data.vouchersQuantity !== null && data.vouchersQuantity !== undefined && selectedCodeVoucherOption === 'import') {
        formattedData.vouchersQuantity = Number(data.vouchersQuantity);
      }

      if (isEditMode && (selectedCodeVoucherOption === 'import' || selectedCodeVoucherOption === 'generate')) {
        const additionalQuantity = Number(data.vouchersQuantity || 0);
        const currentQuantity = Number(row?.stock ?? row?.vouchersQuantity ?? 0);
        formattedData.vouchersQuantity = currentQuantity + additionalQuantity;
        formattedData.stock = formattedData.vouchersQuantity;
      }
    }

    if (data.type === 'parking') {
      if (data.parkingPromoInfo?.value) {
        formattedData.parkingPromoInfo = {
          id: Number(data.parkingPromoInfo.value),
          title: data.parkingPromoInfo.label,
        };
      }
      formattedData.vouchersQuantity = Number(data.vouchersQuantity) || 0;
    }

    if (data.type === 'voucher' || data.type === 'parking') {
      if (data.redemptionLimitPerUser !== undefined && data.redemptionLimitPerUser !== null) {
        formattedData.redemptionLimitPerUser = Number(data.redemptionLimitPerUser);
      }
    }

    if (data.type !== 'parking') {
      if (data.storeEvent === 'store' && transformedData.store) {
        formattedData.store = transformedData.store;
      } else if (
        data.storeEvent === 'restaurant' &&
        transformedData.restaurant &&
        transformedData.restaurant !== null &&
        transformedData.restaurant !== ''
      ) {
        formattedData.restaurant = transformedData.restaurant;
      } else if (data.storeEvent === 'event') {
        if (transformedData.event && transformedData.event !== null && transformedData.event !== '') {
          formattedData.event = transformedData.event;
        }
        if (data.thumb?.url && data.thumb.url !== null && data.thumb.url !== '') {
          formattedData.thumb = data.thumb.url;
        }
      }
    }

    return formattedData;
  };

  const uploadBannerImage = async (data: FormValues, repository: BenefitsRepository) => {
    if (data.banner?.url !== row?.banner) {
      try {
        const bannerResponse = await repository.uploadImage({
          url: data.banner.url?.split(',')[1] || data.banner.url,
          name: data.banner.name,
          type: data.banner.type,
        });

        if (bannerResponse.isLeft()) {
          console.error('Erro na resposta do upload do banner:', bannerResponse.value);
          throw new Error(bannerResponse.value.message || 'Erro ao fazer upload do banner');
        }

        console.log('Upload do banner bem-sucedido:', bannerResponse.value);
        return bannerResponse.value.signedUrl;
      } catch (error) {
        throw new Error('Erro ao fazer upload do banner. Verifique o arquivo e tente novamente.');
      }
    }
    return null;
  };

  const uploadThumbnailImage = async (data: FormValues, repository: BenefitsRepository) => {
    if (data.storeEvent === 'event' && data.thumb?.url && data.thumb.url !== row.thumb) {
      const thumbResponse = await repository.uploadImage({
        url: data.thumb.url?.split(',')[1] || data.thumb.url,
        name: data.thumb.name,
        type: data.thumb.type,
      });

      if (thumbResponse.isLeft()) {
        console.error('Erro na resposta do upload do thumb:', thumbResponse.value);
        throw new Error(thumbResponse.value.message || 'Erro ao fazer upload do thumb');
      }

      return thumbResponse.value.signedUrl;
    }
    if (data.storeEvent === 'event' && data.thumb?.url && data.thumb.url.startsWith('http')) {
      return data.thumb.url;
    }
    return null;
  };

  const extractImportFeedbackSummary = (responseBody: any): ImportFeedbackSummary | null => {
    if (!responseBody || (responseBody.insertedCount === undefined && responseBody.duplicateCodes === undefined)) {
      return null;
    }

    const insertedCount = Number(responseBody.insertedCount ?? 0);
    const duplicateCodes = Array.isArray(responseBody.duplicateCodes)
      ? responseBody.duplicateCodes.map((code: unknown) => String(code))
      : [];
    const duplicateCount = duplicateCodes.length;

    return {
      insertedCount,
      duplicateCodes,
      successCount: insertedCount,
      duplicateCount,
      errorCount: 0,
    };
  };

  const showImportFeedbackToast = (summary: ImportFeedbackSummary) => {
    const { successCount, duplicateCount, duplicateCodes } = summary;
    const duplicateCodesMessage = duplicateCount > 0 ? ` Duplicados: ${duplicateCodes.join(', ')}` : '';

    if (successCount > 0 && duplicateCount === 0) {
      toastSuccess(`Sucesso! ${successCount} código(s) importado(s).`);
      return;
    }

    if (successCount > 0 && duplicateCount > 0) {
      toastInfo(
        ` ℹImportação parcial: ${successCount} código(s) inserido(s), ${duplicateCount} duplicado(s) ignorado(s).${duplicateCodesMessage}`
      );
      return;
    }

    if (successCount === 0 && duplicateCount > 0) {
      toastWarning(`Todos os ${duplicateCount} código(s) já existem no sistema.${duplicateCodesMessage}`);
      return;
    }

    toastWarning('ℹNenhum código novo foi importado.');
  };

  const scrollToFirstError = (errors: Record<string, any>) => {
    const firstErrorKey = Object.keys(errors)[0];
    if (!firstErrorKey) return;
    const el = document.querySelector(`[name="${firstErrorKey}"], #${firstErrorKey}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      (el as HTMLElement).focus?.();
    }
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      setIsLoading(true);
      setImportFeedbackSummary(null);

      if (data.type === 'voucher') {
        const voucherError = validateVoucherType(data);
        if (voucherError) {
          toastWarning(voucherError);
          setIsLoading(false);
          return;
        }
      }

      if (data.type === 'parking') {
        const parkingError = validateParkingType(data);
        if (parkingError) {
          toastWarning(parkingError);
          setIsLoading(false);
          return;
        }
      }

      const transformedData = transformFormData(data);
      const repository = new BenefitsRepository(api);
      const formattedData = buildFormattedData(data, transformedData);

      if (formattedData.type === 'voucher') {
        const fileError = validateVoucherFiles(formattedData.voucherType, formattedData.vouchersQuantity, formattedData.voucherCode);
        if (fileError) {
          throw new Error(fileError);
        }
      }

      console.log('Dados que serão enviados:', JSON.stringify(formattedData, null, 2));

      const bannerUrl = await uploadBannerImage(data, repository);
      if (bannerUrl) {
        formattedData.banner = bannerUrl;
      }

      const thumbUrl = await uploadThumbnailImage(data, repository);
      if (thumbUrl) {
        formattedData.thumb = thumbUrl;
      }

      const shouldPassCsvFile = selectedCodeVoucherOption === 'import' && !!csvFile;

      if (shouldPassCsvFile && csvLineCount === 0) {
        throw new Error('O arquivo CSV está vazio. Adicione ao menos um código para importar.');
      }

      if (row?.id === '-') {
        const response = await repository.createBenefit(formattedData, shouldPassCsvFile ? csvFile : undefined);
        if (response.isLeft()) {
          throw new Error(response.value.message);
        }

        const importSummary = extractImportFeedbackSummary(response.value);
        if (shouldPassCsvFile && importSummary) {
          setImportFeedbackSummary(importSummary);
          showImportFeedbackToast(importSummary);
        } else {
          toastSuccess('Benefício criado com sucesso!');
        }
      } else {
        const response = await repository.updateBenefit(row?.id, formattedData, shouldPassCsvFile ? csvFile : undefined);
        if (response.isLeft()) {
          throw new Error(response.value.message);
        }

        const importSummary = extractImportFeedbackSummary(response.value);
        if (shouldPassCsvFile && importSummary) {
          setImportFeedbackSummary(importSummary);
          showImportFeedbackToast(importSummary);
        } else {
          toastSuccess('Benefício atualizado com sucesso!');
        }
      }

      await handleSuccess();
      getData();
      onClose?.();
    } catch (error) {
      console.error('Erro ao processar benefício:', error);
      if (error instanceof Error) {
        toastWarning(error.message);
      } else {
        toastError('Erro ao importar códigos');
      }
    } finally {
      setIsLoading(false);
    }
  }, scrollToFirstError);

  const handleSuccess = async (): Promise<void> => {
    setHasSuccess(true);
    await delay(1500);
    setHasSuccess(false);
  };

  const handleBannerChange = (row: React.ChangeEvent<HTMLInputElement>) => {
    const allowedImageTypes = ['image/svg+xml', 'image/jpeg', 'image/jpg', 'image/png'];
    try {
      const file = row.target.files?.[0];
      if (file) {
        if (!allowedImageTypes.includes(file.type)) {
          useInvalidFile.onTrue();
          row.target.value = '';
          return;
        }
        const reader = new FileReader();
        reader.onloadend = () => {
          const preview = reader.result?.toString();
          const base64 = preview?.split(',')[1] || '';

          if (!base64) {
            toastWarning('Erro ao processar o arquivo. Tente novamente.');
            return;
          }

          setValue('banner', {
            url: base64,
            name: file.name,
            type: file.type,
          });
        };
        reader.onerror = () => {
          toastWarning('Erro ao processar o arquivo. Tente novamente.');
        };
        reader.readAsDataURL(file);

        // Reset input para permitir selecionar o mesmo arquivo novamente
        // Fazer isso depois de iniciar a leitura, mas antes dela terminar
        setTimeout(() => {
          row.target.value = '';
        }, 100);
      }
    } catch (error) {
      toastWarning('Erro ao selecionar arquivo!');
      row.target.value = '';
    }
  };

  const handleThumbnailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const allowedImageTypes = ['image/svg+xml', 'image/jpeg', 'image/jpg', 'image/png'];
    try {
      const file = event.target.files?.[0];
      if (file) {
        if (!allowedImageTypes.includes(file.type)) {
          useInvalidFile.onTrue();
          event.target.value = '';
          return;
        }
        const reader = new FileReader();
        reader.onloadend = () => {
          const preview = reader.result?.toString();
          const base64 = preview?.split(',')[1] || '';

          if (!base64) {
            toastWarning('Erro ao processar o arquivo. Tente novamente.');
            return;
          }

          setValue('thumb', {
            url: base64,
            name: file.name,
            type: file.type,
          });
        };
        reader.onerror = () => {
          toastWarning('Erro ao processar o arquivo. Tente novamente.');
        };
        reader.readAsDataURL(file);

        // Reset input para permitir selecionar o mesmo arquivo novamente
        setTimeout(() => {
          event.target.value = '';
        }, 100);
      }
    } catch (error) {
      toastWarning('Erro ao selecionar arquivo!');
      event.target.value = '';
    }
  };

  const handleCsvFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const allowedCsvTypes = ['text/csv', 'application/vnd.ms-excel'];
    try {
      const files = Array.from(event.target.files || []);
      const file = files[0];

      if (file) {
        if (!allowedCsvTypes.includes(file.type) && !file.name.endsWith('.csv')) {
          toastWarning('Apenas arquivos CSV são permitidos!');
          event.target.value = '';
          return;
        }

        if (files.length > 1) {
          toastWarning('Apenas um CSV novo por vez. Será considerado somente o primeiro arquivo selecionado.');
        }

        // Contar linhas do CSV
        const reader = new FileReader();
        reader.onload = (e) => {
          const content = e.target?.result as string;
          const lines = content.split('\n').filter((line) => line.trim() !== '');
          const lineCount = Math.max(0, lines.length - 1); // -1 para excluir header
          setCsvLineCount(lineCount);
          setValue('vouchersQuantity', lineCount);
          setValue('stock', lineCount);
        };
        reader.readAsText(file);

        setCsvFile(file);
        toastSuccess(`Arquivo ${file.name} carregado com sucesso!`);

        // Reset input para permitir selecionar o mesmo arquivo novamente
        event.target.value = '';
      }
    } catch (error) {
      toastWarning('Erro ao selecionar arquivo CSV!');
      event.target.value = '';
    }
  };

  const handleButtonClick = (field: 'banner' | 'thumb') => {
    if (field === 'banner') bannerInputRef.current?.click();
    if (field === 'thumb') thumbnailInputRef.current?.click();
  };

  const handleLevelToggle = (level: string) => {
    try {
      const currentLevels = (methods.getValues('levels') || []).filter((l): l is string => l !== undefined);
      const updatedLevels = currentLevels.includes(level) ? currentLevels.filter((l) => l !== level) : [...currentLevels, level];

      setSelectedLevels(updatedLevels);
      methods.setValue('levels', updatedLevels);
    } catch (error) {
      console.error('Erro ao atualizar níveis:', error);
      toastWarning('Erro ao atualizar seleção de níveis');
    }
  };

  const handleAddHowToUseStep = () => {
    const newSteps = [...howToUseSteps, ''];
    setHowToUseSteps(newSteps);
    setValue('howToUse', newSteps);
  };

  const handleRemoveHowToUseStep = (index: number) => {
    if (howToUseSteps.length > 1) {
      const newSteps = howToUseSteps.filter((_, i) => i !== index);
      setHowToUseSteps(newSteps);
      setValue('howToUse', newSteps);
    }
  };

  const handleDiscountTypeToggle = (discount: number) => {
    methods.setValue('discount', discount);
  };

  const handleHowToUseStepChange = (index: number, value: string) => {
    // Passos antigos acima do limite não são cortados: só podem diminuir até o usuário ajustar o texto
    const limitedValue = value.slice(0, Math.max(HOW_TO_USE_STEP_MAX_LENGTH, howToUseSteps[index]?.length ?? 0));
    const newSteps = howToUseSteps.map((step, i) => (i === index ? limitedValue : step));
    setHowToUseSteps(newSteps);
    setValue('howToUse', newSteps);
  };

  const handleRemoveCategory = (value: string) => {
    setSelectedCategories((prev) => prev.filter((cat) => cat.value !== value));
    const currentCategory = methods.getValues('category');
    if (currentCategory === value) {
      methods.setValue('category', '');
    }
  };

  function parseDateTime(iso?: string) {
    try {
      if (!iso) return { date: '', time: '' };

      const d = new Date(iso);

      if (Number.isNaN(d.getTime())) {
        console.warn('Data inválida:', iso);
        return { date: '', time: '' };
      }

      const date = d.toISOString().split('T')[0];
      const time = `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;

      return { date, time };
    } catch (error) {
      console.error('Erro ao processar data:', error);
      return { date: '', time: '' };
    }
  }

  // inicia formulario
  useEffect(() => {
    if (!row) return;

    try {
      const { date, time } = parseDateTime(row.date);
      let categoryValue = '';
      if (typeof row.category === 'object' && row.category?.$oid) {
        categoryValue = row.category.$oid;
      } else if (typeof row.category === 'string') {
        categoryValue = row.category;
      } else if (Array.isArray(row.category) && row.category.length > 0) {
        categoryValue = row.category[0].$oid || row.category[0];
      }
      let levelArray: string[] = [];
      if (Array.isArray(row.levels)) {
        levelArray = row.levels.map((level: any) => level.$oid || level);
      } else if (row.levels) {
        levelArray = [row.levels.$oid || row.levels];
      }

      const formData = {
        ...row,
        category: categoryValue,
        levels: levelArray,
        store: row.store || null,
        restaurant: row.restaurant || null,
        event: row.event || null,
        discountType: row.discountType,
        discountTitle: row.discountTitle || '',
        discountSubtitle: row.discountSubtitle || '',
        discount: row.discount || 0,
        voucherType: row.voucherType || 'generate',
        voucherCode: row.voucherCode || '',
        vouchersQuantity: row?.id && row.id !== '-' && row?.type !== 'parking' ? 0 : row.vouchersQuantity || 0,
        stock: row.stock || 0,
        parkingPromoInfo: row.parkingPromoInfo?.id ? { value: row.parkingPromoInfo.id, label: row.parkingPromoInfo.title } : null,
        redemptionLimitPerUser: row.redemptionLimitPerUser ?? 1,
        banner: typeof row.banner === 'object' && row.banner !== null ? row.banner : { url: row.banner ?? '', name: '', type: '' },
        thumb: typeof row.thumb === 'object' && row.thumb !== null ? row.thumb : { url: row.thumb ?? '', name: '', type: '' },
        startDate: typeof row.startDate === 'object' && row.startDate?.$date ? row.startDate.$date : row.startDate || '',
        endDate: typeof row.endDate === 'object' && row.endDate?.$date ? row.endDate.$date : row.endDate || '',
        date,
        time,
      };

      methods.reset(formData);

      if (levelArray.length > 0 && levelsOptions.length > 0) {
        setSelectedLevels(levelArray);
        setTimeout(() => {
          methods.setValue('levels', levelArray);
        }, 100);
      }
    } catch (error) {
      console.error('Erro ao carregar dados do benefício:', error);
      toastWarning('Erro ao carregar dados do benefício');
    }
  }, [row, methods, toastWarning, levelsOptions]);

  // store/restaurant/event
  useEffect(() => {
    if (!row) return;

    if (row.storeEvent === 'store') {
      setSelectedStoreOption('select');
    } else if (row.storeEvent === 'event') {
      setSelectedStoreOption('manual');
    } else if (row.storeEvent === 'restaurant') {
      setSelectedStoreOption('restaurant');
    } else {
      setSelectedStoreOption('');
    }
  }, [row]);

  // como usar
  useEffect(() => {
    if (!row) return;

    if (row.howToUse && Array.isArray(row.howToUse) && row.howToUse.length > 0) {
      setHowToUseSteps(row.howToUse);
    } else {
      setHowToUseSteps(['']);
    }
  }, [row]);

  // tipo de voucher
  useEffect(() => {
    if (!row) return;

    if (row.voucherType === 'import') {
      setSelectedCodeVoucherOption('import');
    } else if (row.voucherType === 'generate') {
      setSelectedCodeVoucherOption('generate');
    } else if (row.voucherType === 'uniqueCode') {
      setSelectedCodeVoucherOption('uniqueCode');
    } else {
      setSelectedCodeVoucherOption('');
    }

    if (row.discountType) {
      setSelectedVoucherOption(row.discountType);
    }
  }, [row]);

  // sync category
  useEffect(() => {
    if (!row || !row.category || categoriesOptions.length === 0) return;

    let categoryId = '';
    if (typeof row.category === 'object' && row.category?.$oid) {
      categoryId = row.category.$oid;
    } else if (Array.isArray(row.category) && row.category.length > 0) {
      categoryId = row.category[0]?.$oid || row.category[0];
    } else if (typeof row.category === 'string') {
      categoryId = row.category;
    }

    const currentCategory = methods.getValues('category');
    if ((!currentCategory || typeof currentCategory === 'string') && categoryId) {
      const categoryOption = categoriesOptions.find((cat) => cat.value === categoryId);
      if (categoryOption) {
        methods.setValue('category', categoryOption.value);
      }
    }
  }, [row, categoriesOptions, methods]);

  // sync stores
  useEffect(() => {
    if (!row || !row.store || storesOptions.length === 0) return;

    let storeId = '';
    if (typeof row.store === 'object' && row.store?.$oid) {
      storeId = row.store.$oid;
    } else if (Array.isArray(row.store) && row.store.length > 0) {
      storeId = row.store[0]?.$oid || row.store[0];
    } else if (typeof row.store === 'string') {
      storeId = row.store;
    }

    const currentStore = methods.getValues('store');
    if ((!currentStore || typeof currentStore === 'string') && storeId) {
      const storeOption = storesOptions.find((store) => store.value === storeId);
      if (storeOption) {
        methods.setValue('store', storeOption.value);
      }
    }
  }, [row, storesOptions, methods]);

  // sync restaurants
  useEffect(() => {
    if (!row || !row.restaurant || restaurantsOptions.length === 0) return;

    let restaurantId = '';
    if (typeof row.restaurant === 'object' && row.restaurant?.$oid) {
      restaurantId = row.restaurant.$oid;
    } else if (Array.isArray(row.restaurant) && row.restaurant.length > 0) {
      restaurantId = row.restaurant[0]?.$oid || row.restaurant[0];
    } else if (typeof row.restaurant === 'string') {
      restaurantId = row.restaurant;
    }

    const currentRestaurant = methods.getValues('restaurant');
    if ((!currentRestaurant || typeof currentRestaurant === 'string') && restaurantId) {
      const restaurantOption = restaurantsOptions.find((restaurant) => restaurant.value === restaurantId);
      if (restaurantOption) {
        methods.setValue('restaurant', restaurantOption.value);
      }
    }
  }, [row, restaurantsOptions, methods]);

  // sync parkingPromoInfo
  useEffect(() => {
    if (!row || !row.parkingPromoInfo?.id || promotionOptions.length === 0) return;

    const currentPromoInfo = methods.getValues('parkingPromoInfo');
    if (!currentPromoInfo) {
      const promoOption = promotionOptions.find((promo) => promo.value === row.parkingPromoInfo.id);
      if (promoOption) {
        methods.setValue('parkingPromoInfo', promoOption);
      }
    }
  }, [row, promotionOptions, methods]);

  // sync levels
  useEffect(() => {
    if (!row || levelsOptions.length === 0 || hasInitializedLevels) return;

    let levelArray: string[] = [];
    if (Array.isArray(row.levels)) {
      levelArray = row.levels.map((level: any) => {
        if (typeof level === 'object') {
          return level.$oid || level._id?.$oid || level._id || level;
        }
        return level;
      });
    } else if (row.levels) {
      if (typeof row.levels === 'object') {
        levelArray = [row.levels.$oid || row.levels._id?.$oid || row.levels._id || row.levels];
      } else {
        levelArray = [row.levels];
      }
    }

    if (levelArray.length > 0) {
      const currentLevels = methods.getValues('levels') || [];

      if (currentLevels.length !== levelArray.length || !levelArray.every((level) => currentLevels.includes(level))) {
        methods.setValue('levels', levelArray);
        setSelectedLevels(levelArray);
        methods.trigger('levels');
      }
    }

    setHasInitializedLevels(true);
  }, [row, levelsOptions, methods, hasInitializedLevels]);

  const typeOptions = [
    { value: 'informative', label: 'Informativo' },
    { value: 'voucher', label: 'Voucher' },
    { value: 'parking', label: 'Estacionamento' },
  ];

  useEffect(() => {
    const benefitsRepository = new BenefitsRepository(api);

    const loadOptions = async () => {
      try {
        const categoriesResponse = await benefitsRepository.getCategories();
        if (categoriesResponse.isRight()) {
          const categoriesData = categoriesResponse.value?.data || categoriesResponse.value || [];
          setCategoriesOptions(
            categoriesData.map((cat: any) => ({
              value: cat._id?.$oid || cat._id || cat.id,
              label: cat.name || 'Categoria',
            }))
          );
        }

        const storesResponse = await benefitsRepository.getStores();
        if (storesResponse.isRight()) {
          const storesData = storesResponse.value?.data || storesResponse.value || [];

          if (Array.isArray(storesData)) {
            setStoresOptions(
              storesData.map((store: any) => ({
                value: store._id?.$oid || store._id || store.id,
                label: store.name || 'Loja sem nome',
              }))
            );
          } else {
            console.warn('Stores data is not an array:', storesData);
            setStoresOptions([]);
          }
        } else {
          console.error('Error fetching stores:', storesResponse);
          setStoresOptions([]);
        }

        const restaurantsResponse = await benefitsRepository.getRestaurants();
        if (restaurantsResponse.isRight()) {
          const restaurantsData = restaurantsResponse.value?.data || restaurantsResponse.value || [];

          if (Array.isArray(restaurantsData)) {
            setRestaurantsOptions(
              restaurantsData.map((restaurant: any) => ({
                value: restaurant._id?.$oid || restaurant._id || restaurant.id,
                label: restaurant.name || 'Restaurante sem nome',
              }))
            );
          } else {
            console.warn('Restaurants data is not an array:', restaurantsData);
            setRestaurantsOptions([]);
          }
        } else {
          console.error('Error fetching restaurants:', restaurantsResponse);
          setRestaurantsOptions([]);
        }

        const levelsResponse = await benefitsRepository.getLevels({ status: 'active' });
        if (levelsResponse.isRight()) {
          const levelsData = levelsResponse.value?.data || levelsResponse.value || [];
          if (Array.isArray(levelsData)) {
            const mappedLevels = levelsData.map((level: any, index: number) => ({
              value: level._id?.$oid || level._id || level.id || `level-${index}`,
              label: level.label || level.level || level.name || 'Nível',
            }));

            setLevelsOptions(mappedLevels);
          } else {
            setLevelsOptions([]);
          }
        } else {
          console.error('Error fetching levels:', levelsResponse);
          setLevelsOptions([]);
        }

        const promotionResponse = await benefitsRepository.getPromotionOptions();
        if (promotionResponse.isRight()) {
          const promotionsData = promotionResponse.value || [];
          if (Array.isArray(promotionsData)) {
            const mappedPromotions = promotionsData.map((promo: any, index: number) => ({
              value: promo._id?.$oid || promo._id || promo.id || `promo-${index}`,
              label: promo.title || promo.name || 'Promoção',
            }));
            setPromotionOptions(mappedPromotions);
          } else {
            setPromotionOptions([]);
          }
        } else {
          console.error('Error fetching promotion options:', promotionResponse);
          setPromotionOptions([]);
        }
      } catch (error) {
        console.error('Erro ao carregar opções:', error);
        toastWarning('Erro ao carregar dados do formulário');
      }
    };

    loadOptions();
  }, [api, toastWarning]);

  useEffect(() => {
    setValue('howToUse', howToUseSteps);
  }, [howToUseSteps, setValue]);

  useEffect(() => {
    const fetchVoucherStats = async () => {
      try {
        if (!row?.id || row.id === '-' || !['voucher', 'parking'].includes(row.type)) {
          setVoucherStats({ total: 0, redeemed: 0, used: 0 });
          return;
        }

        const repository = new BenefitsRepository(api);
        const response = await repository.getVoucherStats(row.id);
        if (response.isLeft()) {
          console.error('Erro ao buscar estatísticas de vouchers:', response.value.message);
          return;
        }
        setVoucherStats({
          total: Number(response.value?.total ?? 0),
          redeemed: Number(response.value?.redeemed ?? 0),
          used: Number(response.value?.used ?? 0),
        });
      } catch (error) {
        console.error('Erro ao buscar estatísticas de vouchers:', error);
      }
    };

    fetchVoucherStats();
  }, [row, api]);

  const typeValue = watch('type');
  const storeEventValue = watch('storeEvent');

  useEffect(() => {
    if (typeValue === 'parking') {
      setSelectedCodeVoucherOption('');
      setSelectedVoucherOption('');
    }
  }, [typeValue]);

  const handleStoreOptionChange = (option: 'store' | 'restaurant' | 'event') => {
    if (option === 'store') {
      setValue('storeEvent', 'store');
    } else if (option === 'restaurant') {
      setValue('storeEvent', 'restaurant');
    } else {
      setValue('storeEvent', 'event');
    }

    if (option === 'store') {
      setSelectedStoreOption('select');
    } else if (option === 'restaurant') {
      setSelectedStoreOption('restaurant');
    } else {
      setSelectedStoreOption('manual');
    }

    if (option !== 'store') {
      setValue('store', null);
    }
    if (option !== 'restaurant') {
      setValue('restaurant', null);
    }
    if (option !== 'event') {
      setValue('event', null);
    }
  };

  const handleVoucherCodeTypeToggle = (option: 'generate' | 'import' | 'uniqueCode') => {
    const isImportedVoucherInEdit = isEditMode && row?.type === 'voucher' && row?.voucherType === 'import';
    if (isImportedVoucherInEdit && option === 'uniqueCode') {
      return;
    }

    setSelectedCodeVoucherOption(option);
    setValue('voucherType', option);
    if (option !== 'import') {
      setCsvFile(null);
      setCsvLineCount(0);
    }
    if (option === 'import') {
      if (!isEditMode) {
        setValue('stock', csvLineCount);
        setValue('vouchersQuantity', csvLineCount);
      } else {
        setValue('vouchersQuantity', csvLineCount);
      }
    }
    if (option === 'generate') {
      setValue('stock', undefined);
    }
  };

  const handleDownloadVoucherCodes = async () => {
    try {
      setIsLoading(true);

      if (!row?.id || row.id === '-') {
        toastWarning('Salve o benefício primeiro para gerar a lista de códigos');
        return;
      }

      const repository = new BenefitsRepository(api);
      const vouchersResponse = await repository.getVouchersByBenefitId(row.id);

      if (vouchersResponse.isLeft()) {
        throw new Error(vouchersResponse.value.message || 'Erro ao buscar vouchers');
      }
      const responseData = vouchersResponse.value;
      let vouchers = [];
      if (Array.isArray(responseData)) {
        vouchers = responseData;
      } else if (responseData?.data && Array.isArray(responseData.data)) {
        vouchers = responseData.data;
      } else if (responseData?.vouchers && Array.isArray(responseData.vouchers)) {
        ({ vouchers } = responseData);
      } else {
        console.warn('Formato de resposta não reconhecido:', responseData);
      }

      if (vouchers.length === 0) {
        toastWarning('Nenhum voucher encontrado para este benefício');
        return;
      }
      const voucherCodesData = [
        ['Código', 'Status', 'Data de Criação', 'Data de Resgate', 'Usuário'],
        ...vouchers.map((voucher: any) => [
          voucher.voucherCode || voucher.code || 'N/A',
          voucher.status === 'redeemed' ? 'Resgatado' : 'Disponível',
          voucher.createdAt ? new Date(voucher.createdAt).toLocaleDateString('pt-BR') : '',
          voucher.redeemedAt ? new Date(voucher.redeemedAt).toLocaleDateString('pt-BR') : '',
          voucher.userId || '',
        ]),
      ];
      const csvContent = voucherCodesData.map((row) => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `vouchers_${row.name?.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      toastSuccess(`Lista com ${vouchers.length} códigos baixada com sucesso!`);
    } catch (error) {
      console.error('Erro ao baixar lista de códigos:', error);
      toastWarning(error instanceof Error ? error.message : 'Erro ao baixar lista de códigos');
    } finally {
      setIsLoading(false);
    }
  };

  const isStockDisabled = useMemo(() => {
    if (typeValue !== 'voucher') return true;
    if (selectedCodeVoucherOption === 'uniqueCode') return true;
    if (selectedCodeVoucherOption === 'import') return true;
    return false;
  }, [typeValue, selectedCodeVoucherOption]);

  const isUniqueCodeOptionDisabled = useMemo(
    () => Boolean(isEditMode && row?.type === 'voucher' && row?.voucherType === 'import'),
    [isEditMode, row]
  );

  const previousCsvFileName = useMemo(
    () =>
      row?.codesFileName ||
      row?.voucherFileName ||
      row?.voucherCodesFileName ||
      row?.csvFileName ||
      row?.csvName ||
      row?.fileName ||
      row?.file?.name ||
      '',
    [row]
  );

  const handleDownloadExampleSheet = () => {
    const exampleData = [['Vouchers'], ['ABC123'], ['ABC124'], ['ABC125']];
    const csvContent = exampleData.map((row) => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `vouchers_padrao.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return {
    methods,
    bannerInputRef,
    thumbnailInputRef,
    errors,
    values,
    hasSuccess,
    isLoading,
    isSubmitting,
    useInvalidFile,
    handleButtonClick,
    handleBannerChange,
    handleThumbnailChange,
    onSubmit,
    typeOptions,
    categoriesOptions,
    levelsOptions,
    storesOptions,
    restaurantsOptions,
    eventsOptions,
    typeValue,
    storeEventValue,
    selectedLevels,
    setSelectedLevels,
    handleLevelToggle,
    handleDiscountTypeToggle,
    howToUseSteps,
    setHowToUseSteps,
    handleAddHowToUseStep,
    handleRemoveHowToUseStep,
    handleHowToUseStepChange,
    selectedStoreOption,
    setSelectedStoreOption,
    handleStoreOptionChange,
    selectedCategories,
    setSelectedCategories,
    handleRemoveCategory,
    selectedVoucherOption,
    setSelectedVoucherOption,
    selectedCodeVoucherOption,
    setSelectedCodeVoucherOption,
    handleVoucherCodeTypeToggle,
    handleDownloadVoucherCodes,
    handleCsvFileChange,
    csvFile,
    setCsvFile,
    csvLineCount,
    isEditMode,
    isStockDisabled,
    isUniqueCodeOptionDisabled,
    previousCsvFileName,
    voucherStats,
    importFeedbackSummary,
    handleDownloadExampleSheet,
    promotionOptions,
  };
};
